import React from 'react';
import { ToggleLabel, Section, SelectLabel, TooltipHelp, CheckboxLabel } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import Style from './ticketManagement.module.css';

export default function TicketTransfer({ form, setForm, identitiesOptions, templatesOptions }) {
    const { t } = useTranslation();

    const [isChecked, setIsChecked] = React.useState(Boolean(form.transfer_settings));

    const changeSetting = React.useCallback(
        (key, value) => {
            setForm('transfer_settings', {
                ...form.transfer_settings,
                [key]: value
            });
        },
        [form.transfer_settings]
    );

    const toggleSetting = React.useCallback(
        key => {
            const newState = !form.transfer_settings[key];
            setForm('transfer_settings', {
                ...form.transfer_settings,
                [key]: newState
            });
        },
        [form.transfer_settings]
    );

    const onClick = React.useCallback(
        e => {
            e.preventDefault();
            setIsChecked(prevState => {
                const newState = !prevState;
                if (newState) {
                    setForm('transfer_settings', {
                        ...form.transfer_settings,
                        allow_tickets_scanned: false,
                        attendee_validation_required: false,
                        allow_cancel: true,
                        allow_force_cancel: false,
                        identity_id: null,
                        mail_template_cancellation_previous_holder: null,
                        notify_cancellation_previous_holder: false,
                        notify_attendee_new_transfer: false,
                        mail_template_ticket_transfer: null
                    });
                } else {
                    setForm('transfer_settings', null);
                }

                return newState;
            });
        },
        [form.transfer_settings]
    );

    return (
        <>
            <span onClick={onClick}>
                <ToggleLabel
                    inlinePosition="left"
                    checked={isChecked}
                    className={Style['swappable-config-container']}
                    label={t('ticket.rates.ticket-transfer.title')}
                    subtitle={t('ticket.rates.ticket-transfer.subtitle')}
                />
            </span>
            {isChecked && (
                <Section>
                    <SelectLabel
                        value={form.transfer_settings.identity_id}
                        label={t('ticket.waiting_list.create.emails_and_notifications.sender_label')}
                        placeholder={t('ticket.waiting_list.create.emails_and_notifications.sender_placeholder')}
                        onChange={(e, { value }) => {
                            changeSetting('identity_id', value);
                        }}
                        options={identitiesOptions}
                    />
                    <CheckboxLabel
                        label={t('ticket.rates.ticket-transfer.notify_attendee_new_transfer.label')}
                        onChange={event => {
                            const stateToggle = event.target.checked;
                            setForm('transfer_settings', {
                                ...form.transfer_settings,
                                notify_attendee_new_transfer: stateToggle,
                                ...(!stateToggle && { mail_template_ticket_transfer: null })
                            });
                        }}
                        checked={form.transfer_settings.notify_attendee_new_transfer}
                    />
                    {form.transfer_settings.notify_attendee_new_transfer && (
                        <SelectLabel
                            value={form.transfer_settings.mail_template_ticket_transfer}
                            label={t('ticket.waiting_list.create.emails_and_notifications.email_template_label')}
                            placeholder={t('ticket.waiting_list.create.emails_and_notifications.email_template_placeholder')}
                            onChange={(e, { value }) => {
                                changeSetting('mail_template_ticket_transfer', value);
                            }}
                            options={templatesOptions}
                        />
                    )}
                    <CheckboxLabel
                        label={
                            <div>
                                {t('ticket.rates.ticket-transfer.notify_cancellation_previous_holder.label')}
                                <TooltipHelp title={t('ticket.rates.ticket-transfer.notify_cancellation_previous_holder.tooltip')} />
                            </div>
                        }
                        onChange={event => {
                            const stateToggle = event.target.checked;
                            setForm('transfer_settings', {
                                ...form.transfer_settings,
                                notify_cancellation_previous_holder: stateToggle,
                                ...(!stateToggle && { mail_template_cancellation_previous_holder: null })
                            });
                        }}
                        checked={form.transfer_settings.notify_cancellation_previous_holder}
                    />
                    {form.transfer_settings.notify_cancellation_previous_holder && (
                        <SelectLabel
                            value={form.transfer_settings.mail_template_cancellation_previous_holder}
                            label={t('ticket.waiting_list.create.emails_and_notifications.email_template_label')}
                            placeholder={t('ticket.waiting_list.create.emails_and_notifications.email_template_placeholder')}
                            onChange={(e, { value }) => {
                                changeSetting('mail_template_cancellation_previous_holder', value);
                            }}
                            options={templatesOptions}
                        />
                    )}
                    <CheckboxLabel
                        label={
                            <div>
                                {t('ticket.rates.ticket-transfer.allow_tickets_scanned.label')}
                                <TooltipHelp title={t('ticket.rates.ticket-transfer.allow_tickets_scanned.tooltip')} />
                            </div>
                        }
                        onChange={() => toggleSetting('allow_tickets_scanned')}
                        checked={form.transfer_settings.allow_tickets_scanned}
                    />
                    <CheckboxLabel
                        label={
                            <div>
                                {t('ticket.rates.ticket-transfer.attendee_validation_required.label')}
                                <TooltipHelp title={t('ticket.rates.ticket-transfer.attendee_validation_required.tooltip')} />
                            </div>
                        }
                        onChange={() => toggleSetting('attendee_validation_required')}
                        checked={form.transfer_settings.attendee_validation_required}
                    />
                    <CheckboxLabel
                        label={t('ticket.rates.ticket-transfer.allow_cancel.label')}
                        onChange={() => toggleSetting('allow_cancel')}
                        checked={form.transfer_settings.allow_cancel}
                    />
                    <CheckboxLabel
                        label={
                            <div>
                                {t('ticket.rates.ticket-transfer.allow_force_cancel.label')}
                                <TooltipHelp title={t('ticket.rates.ticket-transfer.allow_force_cancel.tooltip')} />
                            </div>
                        }
                        onChange={() => toggleSetting('allow_force_cancel')}
                        checked={form.transfer_settings.allow_force_cancel}
                    />
                </Section>
            )}
        </>
    );
}
